import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Trophy,
  Zap,
  CheckCircle,
  XCircle,
  Home,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Share2,
  ExternalLink,
  BookOpen,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const ResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser, user } = useAuth();
  const result = location.state?.result;
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  useEffect(() => {
    if (result && result.score >= 60) {
      setTimeout(() => {
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 },
        });
      }, 500);
    }
    
    if (result) {
      updateUser({ total_gp: (user?.total_gp || 0) + (result?.gp_earned || 0) });
    }
  }, []);

  if (!result) {
    navigate("/dashboard");
    return null;
  }

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-500";
    if (score >= 60) return "text-amber-500";
    return "text-red-500";
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return "Outstanding! You're a Tax Genius! 🏆";
    if (score >= 80) return "Excellent! Keep up the great work! 🌟";
    if (score >= 60) return "Good job! Room for improvement! 💪";
    if (score >= 40) return "Keep learning! You'll get better! 📚";
    return "Don't give up! Practice makes perfect! 🎯";
  };

  const SHARE_URL = "https://www.taxgenius.online";
  const buildShareText = () =>
    `🎯 I scored ${result?.score || 0}% on Tax Genius! Got ${result?.correct_count || 0}/${result?.total_questions || 15} correct and earned ${result?.gp_earned || 0} GP! 🏆 Test your Indian Tax knowledge at ${SHARE_URL}`;

  const shareOnWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, "_blank");
    toast.success("Opening WhatsApp...");
  };

  const shareOnTwitter = () => {
    const text = `🎯 I scored ${result?.score || 0}% on Tax Genius! Got ${result?.correct_count || 0}/${result?.total_questions || 15} correct & earned ${result?.gp_earned || 0} GP 🏆`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SHARE_URL)}`, "_blank");
    toast.success("Opening Twitter...");
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}`, "_blank");
    toast.success("Opening LinkedIn...");
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(buildShareText());
    toast.success("Copied to clipboard!");
  };

  const details = result.results || result.details || [];
  const incorrectAnswers = details.filter((d) => !d.is_correct);

  return (
    <div className="min-h-screen bg-amber-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="bg-white rounded-3xl shadow-2xl border-0 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
                className="w-24 h-24 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center"
              >
                <Trophy className="w-12 h-12 text-white" />
              </motion.div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Nunito' }}>
                Quiz Complete!
              </h1>
              <p className="text-emerald-100">{getScoreMessage(result.score)}</p>
            </div>

            <CardContent className="p-8">
              {/* Score Display */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <span className={`text-7xl font-bold ${getScoreColor(result.score)}`} style={{ fontFamily: 'Nunito' }}>
                    {result.score}%
                  </span>
                </motion.div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-emerald-50 rounded-2xl">
                  <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800" data-testid="correct-count">
                    {result.correct_count}
                  </p>
                  <p className="text-sm text-slate-600">Correct</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-2xl">
                  <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800" data-testid="incorrect-count">
                    {result.total_questions - result.correct_count}
                  </p>
                  <p className="text-sm text-slate-600">Incorrect</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-2xl">
                  <Zap className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800" data-testid="gp-earned">
                    +{result.gp_earned}
                  </p>
                  <p className="text-sm text-slate-600">GP Earned</p>
                </div>
              </div>

              {/* GP Breakdown */}
              {result.bonuses && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <p className="font-bold text-slate-700">GP Breakdown</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">📚 Base (difficulty-weighted)</span>
                      <span className="font-semibold text-slate-800">+{result.bonuses.base} GP</span>
                    </div>
                    {result.bonuses.streak_bonus > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">🔥 Streak Bonus (best: {result.bonuses.max_streak} in a row)</span>
                        <span className="font-semibold text-emerald-600">+{result.bonuses.streak_bonus} GP</span>
                      </div>
                    )}
                    {result.bonuses.time_bonus > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">⚡ Speed Bonus</span>
                        <span className="font-semibold text-emerald-600">+{result.bonuses.time_bonus} GP</span>
                      </div>
                    )}
                    {(() => {
                      const repeatCount = (result.results || []).filter(r => r.is_repeat).length;
                      return repeatCount > 0 ? (
                        <div className="flex justify-between items-center">
                          <span className="text-red-500">🚫 Repeat questions ({repeatCount})</span>
                          <span className="font-semibold text-red-500">0 GP</span>
                        </div>
                      ) : null;
                    })()}
                    <div className="border-t border-amber-200 pt-2 flex justify-between items-center font-bold">
                      <span className="text-slate-700">Total Earned</span>
                      <span className="text-amber-600 text-base">+{result.gp_earned} GP</span>
                    </div>
                  </div>
                  {result.bonuses.max_streak >= 3 && (
                    <p className="text-xs text-emerald-600 mt-2 text-center">
                      🔥 {result.bonuses.max_streak}-question streak! Keep it up!
                    </p>
                  )}
                  {(() => {
                    const repeatCount = (result.results || []).filter(r => r.is_repeat).length;
                    return repeatCount > 0 ? (
                      <p className="text-xs text-red-500 mt-2 text-center">
                        🚫 {repeatCount} question{repeatCount > 1 ? "s" : ""} already answered before — no GP awarded for repeats. Explore new questions to keep earning!
                      </p>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Social Share */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Share2 className="w-5 h-5 text-indigo-600" />
                  <p className="font-bold text-slate-800">Share Your Score!</p>
                </div>
                <p className="text-slate-600 text-sm mb-4">{buildShareText()}</p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={shareOnWhatsApp}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-full px-4"
                    data-testid="share-whatsapp-btn"
                  >
                    WhatsApp
                  </Button>
                  <Button
                    onClick={shareOnTwitter}
                    className="bg-blue-400 hover:bg-blue-500 text-white rounded-full px-4"
                    data-testid="share-twitter-btn"
                  >
                    Twitter
                  </Button>
                  <Button
                    onClick={shareOnLinkedIn}
                    className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-4"
                    data-testid="share-linkedin-btn"
                  >
                    LinkedIn
                  </Button>
                  <Button
                    onClick={copyShareLink}
                    variant="outline"
                    className="rounded-full px-4"
                    data-testid="share-copy-btn"
                  >
                    Copy Link
                  </Button>
                </div>
              </div>

              {/* Weak Topics — sourced from result.incorrect_topics built at submit time */}
              {result.incorrect_topics && result.incorrect_topics.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800">Areas to Improve</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Questions you got wrong this round — keep practising these topics!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate("/quiz")}
                  className="flex-1 btn-primary-3d text-white font-bold rounded-full py-6"
                  data-testid="play-again-btn"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Play Again
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="flex-1 rounded-full py-6 font-bold"
                  data-testid="back-to-dashboard-btn"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Incorrect Answers - Learn Section */}
        {incorrectAnswers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>
                Learn From Your Mistakes ({incorrectAnswers.length})
              </h2>
            </div>
            <p className="text-slate-600 text-sm mb-4">
              Review these questions and click on the reference links to learn more from the actual Income Tax Act
            </p>

            <div className="space-y-4">
              {incorrectAnswers.map((detail, index) => (
                <Card
                  key={index}
                  className="rounded-2xl border-2 border-rose-200 bg-white overflow-hidden"
                >
                  <CardContent className="p-0">
                    <div className="bg-rose-50 p-4 border-b border-rose-100">
                      <div className="flex items-start gap-3">
                        <XCircle className="w-6 h-6 text-rose-500 flex-shrink-0 mt-1" />
                        <p className="font-semibold text-slate-800">{detail.question}</p>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      {/* Show correct vs wrong answer */}
                      <div className="grid gap-2 mb-4">
                        <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="text-sm">
                            Your answer: <span className="font-medium">{detail.options[detail.your_answer] || "Not answered"}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm">
                            Correct answer: <span className="font-medium">{detail.options[detail.correct_answer]}</span>
                          </span>
                        </div>
                      </div>

                      {/* Explanation */}
                      <div className="bg-slate-50 rounded-xl p-4 mb-4">
                        <p className="text-sm font-semibold text-slate-700 mb-1">Explanation:</p>
                        <p className="text-sm text-slate-600">{detail.explanation}</p>
                      </div>

                      {/* Reference Link */}
                      {detail.reference_url && (
                        <a
                          href={detail.reference_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-3 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors"
                        >
                          <ExternalLink className="w-5 h-5 text-indigo-600" />
                          <div>
                            <p className="font-semibold text-indigo-700 text-sm">
                              {detail.learn_more || "Learn more in the Income Tax Act"}
                            </p>
                            {detail.reference?.section && (
                              <p className="text-xs text-indigo-500">
                                Section {detail.reference.section} - {detail.reference.act}
                              </p>
                            )}
                          </div>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* All Answers Review */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito' }}>
            Full Answer Review
          </h2>

          <div className="space-y-4">
            {details.map((detail, index) => (
              <Card
                key={index}
                className={`rounded-2xl border-2 ${
                  detail.is_correct ? "border-emerald-200" : "border-slate-200"
                }`}
              >
                <CardContent className="p-4">
                  <button
                    onClick={() =>
                      setExpandedQuestion(expandedQuestion === index ? null : index)
                    }
                    className="w-full text-left"
                    data-testid={`review-question-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            detail.is_correct ? "bg-emerald-100" : "bg-slate-100"
                          }`}
                        >
                          {detail.is_correct ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">
                            Q{index + 1}: {detail.question.substring(0, 80)}
                            {detail.question.length > 80 ? "..." : ""}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            detail.is_correct 
                              ? "bg-emerald-100 text-emerald-700" 
                              : "bg-slate-100 text-slate-600"
                          }`}>
                            {detail.topic?.replace(/_/g, " ")}
                          </span>
                        </div>
                      </div>
                      {expandedQuestion === index ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {expandedQuestion === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-4 pt-4 border-t border-slate-200"
                    >
                      <p className="text-slate-700 mb-4">{detail.question}</p>

                      <div className="space-y-2 mb-4">
                        {detail.options.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className={`p-3 rounded-xl text-sm ${
                              optIndex === detail.correct_answer
                                ? "bg-emerald-100 text-emerald-800 font-medium"
                                : optIndex === detail.your_answer && !detail.is_correct
                                ? "bg-red-100 text-red-800"
                                : "bg-slate-50 text-slate-600"
                            }`}
                          >
                            <span className="font-bold mr-2">
                              {String.fromCharCode(65 + optIndex)}:
                            </span>
                            {option}
                            {optIndex === detail.correct_answer && (
                              <span className="ml-2">✓ Correct</span>
                            )}
                            {optIndex === detail.your_answer &&
                              optIndex !== detail.correct_answer && (
                                <span className="ml-2">✗ Your answer</span>
                              )}
                          </div>
                        ))}
                      </div>

                      <div className="bg-slate-50 p-4 rounded-xl">
                        <p className="text-sm font-semibold text-slate-700 mb-1">
                          Explanation:
                        </p>
                        <p className="text-sm text-slate-600">{detail.explanation}</p>
                      </div>
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsPage;
